import { cloneRulebook, getErrorType, importPublishedRulebook, NotificationType, useTranslation } from "@next-platform/client-common-core";
import { RulebookErrorType } from "@next-platform/packages-ts-types";
import type { AxiosError } from "axios";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import resolveRulebookCloneOutcome, {
  RULEBOOK_CLONE_NOT_OPENED_OUTCOME,
  type RulebookCloneOutcome,
} from "@/components/search/utils/resolveRulebookCloneOutcome";
import { RULEBOOK_EDITOR_FOCUS_NAME_STATE } from "@/pages/RulebookEditor/utils/isRulebookEditorFocusNameState";

import useToast from "./useToast";

/** A rejected import still left the clone on the server — resolve to the not-opened outcome. */
const importCloneOutcome = (rulebookId: string): Promise<RulebookCloneOutcome> =>
  importPublishedRulebook({ rulebookId })
    .then((result) => resolveRulebookCloneOutcome(result))
    .catch((): RulebookCloneOutcome => RULEBOOK_CLONE_NOT_OPENED_OUTCOME);

const CLONE_BLOCKED_REVIEW = "blocked_review" as const;

const isCloneBlockedInReview = (error: unknown): boolean => {
  const body = (error as AxiosError<{ errorCode?: unknown }>)?.response?.data;
  return typeof body?.errorCode === "string" && getErrorType(body.errorCode, RulebookErrorType) === RulebookErrorType.IN_REVIEW;
};

/**
 * Clones a public rulebook server-side, pulls the copy into a local editor draft, and navigates
 * to it. `cloneRulebook` rejecting toasts `cloneFailed`; a rejected or non-"imported" import still
 * left the copy on the server, so that path toasts `clonedNotOpened` and points at My Rulebooks.
 */
export default function useCloneRulebook(): { clone: (rulebookId: string) => void; isCloning: boolean } {
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const navigate = useNavigate();
  const [stateIsCloning, setStateIsCloning] = useState(false);

  const clone = useCallback(
    (rulebookId: string) => {
      if (stateIsCloning) {
        return;
      }
      setStateIsCloning(true);
      cloneRulebook(rulebookId)
        .then(
          (writeResult) => importCloneOutcome(writeResult.rulebook.id),
          // The clone request itself failed — nothing exists on the server.
          (error) => (isCloneBlockedInReview(error) ? CLONE_BLOCKED_REVIEW : null),
        )
        .then((outcome) => {
          if (outcome === CLONE_BLOCKED_REVIEW) {
            displayToast({ type: NotificationType.error, message: t("rulebook:card.actions.cloneBlockedReview") });
            return;
          }
          if (outcome === null) {
            displayToast({ type: NotificationType.error, message: t("rulebook:explore.cloneFailed") });
            return;
          }
          if (!outcome.ok) {
            displayToast({ type: NotificationType.error, message: t("rulebook:explore.clonedNotOpened") });
            navigate(outcome.fallbackPath);
            return;
          }
          displayToast({ type: NotificationType.success, message: t("rulebook:explore.cloned") });
          navigate(outcome.path, { state: RULEBOOK_EDITOR_FOCUS_NAME_STATE });
        })
        .catch(() => displayToast({ type: NotificationType.error, message: t("rulebook:explore.cloneFailed") }))
        .finally(() => setStateIsCloning(false));
    },
    [stateIsCloning, displayToast, navigate, t],
  );

  return { clone, isCloning: stateIsCloning };
}
